import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { portalApi } from '../services/portalApi';
import './PortalInvoiceDetailPage.css';

function money(v) { return `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2 })}` }
function fmtDate(d) { return d ? new Date(d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '—' }

export default function PortalInvoiceDetailPage() {
  const { id }    = useParams();
  const navigate  = useNavigate();
  const location  = useLocation();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const pollRef = useRef(null);

  const stripeStatus = new URLSearchParams(location.search).get('redirect_status');
  const returnedFromStripe = stripeStatus === 'succeeded';

  useEffect(() => {
    portalApi.invoice(id)
      .then(data => {
        setInvoice(data);
        // Stripe redirects back with ?redirect_status=succeeded before the webhook fires.
        // Poll until the invoice flips to Paid so we can show accurate status.
        if (returnedFromStripe && data?.status !== 'Paid') startPolling();
      })
      .catch(() => setError('Invoice not found.'))
      .finally(() => setLoading(false));

    return () => clearInterval(pollRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  function startPolling() {
    let attempts = 0;
    pollRef.current = setInterval(async () => {
      attempts++;
      if (attempts > 12) { clearInterval(pollRef.current); return; }
      try {
        const data = await portalApi.invoice(id);
        setInvoice(data);
        if (data?.status === 'Paid') clearInterval(pollRef.current);
      } catch { clearInterval(pollRef.current); }
    }, 5000);
  }

  if (loading) return <div className="pid-page"><div className="pid-loading"><div className="pid-spin" /></div></div>;
  if (error)   return <div className="pid-page"><div className="pid-error-msg">{error}</div></div>;

  const isPaid   = invoice.status === 'Paid';
  const canPay   = !isPaid && !!invoice.stripePaymentLinkId;

  return (
    <div className="pid-page">
      <button className="pid-back" onClick={() => navigate('/portal')}>← Back</button>

      {returnedFromStripe && !isPaid && (
        <div className="pid-stripe-banner pid-banner-pending">
          <span aria-hidden="true">⏳</span>
          Payment received — confirming with your bank. This page will update automatically.
        </div>
      )}

      {returnedFromStripe && isPaid && (
        <div className="pid-stripe-banner pid-banner-done">
          <span aria-hidden="true">✓</span>
          Payment confirmed. Thank you!
        </div>
      )}

      <div className="pid-header">
        <span className={`pid-badge ${isPaid ? 'pid-badge-paid' : 'pid-badge-unpaid'}`}>
          {isPaid ? 'Paid' : 'Payment due'}
        </span>
        <h1 className="pid-title">{invoice.description}</h1>
        <p className="pid-date">
          {isPaid
            ? `Paid on ${fmtDate(invoice.paidAt)}`
            : `Due ${fmtDate(invoice.dueDate)}`}
        </p>
      </div>

      <div className="pid-amount-card">
        <div className="pid-amount-label">Amount {isPaid ? 'paid' : 'due'}</div>
        <div className="pid-amount">{money(invoice.amountDue)}</div>
      </div>

      <div className="pid-details">
        <div className="pid-detail-row">
          <span>Invoice issued</span>
          <span>{fmtDate(invoice.createdAt)}</span>
        </div>
        <div className="pid-detail-row">
          <span>Due date</span>
          <span>{fmtDate(invoice.dueDate)}</span>
        </div>
        {isPaid && (
          <div className="pid-detail-row">
            <span>Paid on</span>
            <span>{fmtDate(invoice.paidAt)}</span>
          </div>
        )}
        <div className="pid-detail-row">
          <span>Status</span>
          <span className={`pid-status-text ${isPaid ? 'pid-status-paid' : 'pid-status-unpaid'}`}>
            {isPaid ? 'Paid in full' : 'Outstanding'}
          </span>
        </div>
      </div>

      {canPay && (
        <div className="pid-pay-section">
          <p className="pid-pay-note">
            You'll be redirected to our secure payment provider (Stripe) to complete payment.
          </p>
          <a
            href={invoice.stripePaymentLinkId}
            className="pid-pay-btn"
            target="_blank"
            rel="noopener noreferrer"
          >
            Pay {money(invoice.amountDue)} now →
          </a>
        </div>
      )}

      {isPaid && (
        <div className="pid-paid-note">
          <span className="pid-paid-icon" aria-hidden="true">✓</span>
          <p>This invoice has been paid. Thank you for your payment.</p>
        </div>
      )}
    </div>
  );
}
